// src/services/auth.service.ts
import axiosInstance from '../utils/axiosInstance';

export interface UserProfile {
    username: string;
    email: string;
    fullname: string;
    emailVerified: boolean;
    authProvider?: string;
}

export const AuthService = {
    // Standard login
    login: async (username: string, password: string) => {
        const response = await axiosInstance.post('/auth/login', {
            username,
            password
        });

        // Handle new response structure
        const data = response.data?.data || response.data;

        if (data?.access_token) {
            localStorage.setItem('access_token', data.access_token);
        }
        return data;
    },

    // Get user profile
    getProfile: async (): Promise<UserProfile> => {
        const response = await axiosInstance.get('/auth/profile');
        // Handle new response structure
        const data = response.data?.data || response.data;
        return data;
    },

    // LinkedIn OAuth
    getLinkedInAuthUrl: async () => {
        const response = await axiosInstance.get('/auth/linkedin');
        // Handle new response structure
        const data = response.data?.data || response.data;
        return data; // { url: string, state: string }
    },

    // Google OAuth - Match backend response format
    getGoogleAuthUrl: async () => {
        try {
            const response = await axiosInstance.get('/auth/google');

            // Handle new response structure
            const data = response.data?.data || response.data;

            // Validate the response format
            if (!data) {
                throw new Error('No response data received from Google OAuth endpoint');
            }

            // Backend returns { authUrl } format
            if (data.authUrl) {
                return {
                    url: data.authUrl,
                    state: 'google_oauth'
                };
            }

            // Fallback for different response formats
            if (data.url) {
                return {
                    url: data.url,
                    state: data.state || 'google_oauth'
                };
            }

            // If it's a string, it might be the URL directly
            if (typeof data === 'string') {
                return { url: data, state: 'google_oauth' };
            }

            throw new Error('Invalid response format from Google OAuth endpoint');
        } catch (error: any) {
            // If we get a 304, try to extract URL from headers
            if (error.response?.status === 304) {
                const locationHeader = error.response.headers?.location;
                if (locationHeader) {
                    return { url: locationHeader, state: 'google_oauth' };
                }
            }

            throw new Error(`Google OAuth failed: ${error.message}`);
        }
    },

    // Email verification
    verifyEmail: async (token: string) => {
        const response = await axiosInstance.get('/auth/verify-email', {
            params: { token }
        });
        return response.data;
    },

    // Handle OAuth callback
    handleOAuthCallback: async (code: string, state: string, provider: 'google' | 'linkedin') => {
        const response = await axiosInstance.post(`/auth/${provider}/callback`, {
            code,
            state
        });

        // Handle new response structure
        const data = response.data?.data || response.data;

        if (data?.access_token) {
            localStorage.setItem('access_token', data.access_token);
        }
        return data;
    },

    // Reset password
    resetPassword: async (token: string, password: string) => {
        const response = await axiosInstance.post('/auth/reset-password', {
            token,
            password
        });

        const data = response.data?.data || response.data;
        return data;
    },

    // Forgot password
    forgotPassword: async (email: string) => {
        const response = await axiosInstance.post('/auth/forgot-password', {
            email
        });

        // Handle new response structure
        const data = response.data?.data || response.data;
        return data;
    },

    // Change password
    changePassword: async (currentPassword: string, newPassword: string) => {
        const response = await axiosInstance.post('/auth/change-password', {
            currentPassword,
            newPassword
        });

        // Handle new response structure
        const data = response.data?.data || response.data;
        return data;
    },
}
